import { ProfileImage } from 'components/ProfileImage';
import { CommentInput } from 'components/WorkoutCell/components/CommentInput';
import { Comments } from 'components/WorkoutCell/components/Comments';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { stores } from 'state/stores';
import styled from 'styled-components';
import { DividerLine } from 'styleguide/DividerLine';
import { Modal } from 'styleguide/Modal';
import { H5, P2, P3 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { CommentModalViewModel } from './CommentModalViewModel';
import { UserHeader } from '../../../screens/WorkoutDetail/components/UserHeader';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { WorkoutActions } from 'components/WorkoutActions';
import { Stats } from 'components/WorkoutCell/components/Stats';
import { Link } from 'components/Link';
import { FlexRow } from 'styleguide/Row';
import { Icon } from 'styleguide/Icons';
import { colors } from 'styleguide/colors';
import { t } from 'translations/t';

const Container = styled(View)`
  border-radius: ${CornerRadius.md}px;
  width: 600px;
  @media (max-width: 600px) {
    width: 90vw;
  }
`;

const TopContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-bottom: ${Spacing.md}px;
`;

const MiddleContainer = styled(View)`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  flex: 1;
  overflow-y: auto;
`;

const BottomContainer = styled(View)`
  padding: ${Spacing.lg}px;
  padding-top: ${Spacing.md}px;
`;

export interface CommentModalProps {
  vm: CommentModalViewModel;
  isOpen: boolean;
  onClose: () => void;
}

export const CommentModal = observer(({ vm, isOpen, onClose }: CommentModalProps) => {
  const onBottomRefChange = (node: HTMLDivElement) => {
    vm.setCommentsContainerRef(node);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentStyle={{ padding: 0, maxHeight: '90vh' }}>
      <Container>
        <TopContainer>
          <FlexRow style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Link href={`/user/${vm.data.username}`}>
              <View onClick={onClose}>
                <UserHeader
                  username={vm.data.username}
                  date={vm.data.date}
                  profileImage={vm.data.profileImage}
                />
              </View>
            </Link>
            <View style={{ cursor: 'pointer' }} onClick={onClose}>
              <Icon type="cross" tint={colors.grey} />
            </View>
          </FlexRow>

          <Link href={`/workout/${vm.data.shortId || vm.data.workoutId}`}>
            <View style={{ gap: Spacing.md }} onClick={onClose}>
              <View style={{ gap: Spacing.xs }}>
                <H5>{vm.data.title}</H5>
                {vm.data.description && <P3>{vm.data.description}</P3>}
              </View>
              <Stats stats={vm.data.stats} />
            </View>
          </Link>
        </TopContainer>

        <View style={{ padding: `0px ${Spacing.lg}px` }}>
          <DividerLine />
        </View>

        <MiddleContainer>
          {vm.comments.length > 0 ? (
            <View>
              <Comments
                comments={vm.comments}
                onCommentDelete={vm.onCommentDelete}
                onUserClick={() => {
                  onClose();
                }}
                showAllComments={true}
              />
              <div ref={onBottomRefChange} />
            </View>
          ) : (
            <View
              style={{
                flex: 1,
                padding: Spacing.lg * 2,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ProfileImage
                style={{ marginBottom: Spacing.md }}
                diameter={50}
                source={stores.account.profile_pic}
              />
              <P2 type="secondary">{t('web.commentModal.beTheFirst')}</P2>
            </View>
          )}
        </MiddleContainer>

        <WorkoutActions
          isLiked={vm.data.isLiked}
          likeCount={vm.data.likeCount}
          commentCount={vm.data.commentCount}
          onLike={vm.data.onLike}
          onShare={vm.data.onShare}
        />

        <BottomContainer>
          <CommentInput onSend={vm.onCommentSend} profilePic={vm.profileImage} />
        </BottomContainer>
      </Container>
    </Modal>
  );
});
